import { Directive, ViewChild, ViewContainerRef } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';

import { ModalService } from '../../services/modal/modal.service';
import { Subscription } from 'rxjs';
import { ModalConfig, ModalType } from '../../../js/interfaces/CoverpathTypes';

@Directive()
export abstract class ModalContainerBase {
  @ViewChild('childModalRef', { read: ViewContainerRef, static: true }) modalRef;
  modalDisplay: string = '';
  modalSubscription: Subscription;
  modalType: ModalType;
  componentInstance;
  showClose: boolean = true;
  disableEscapeKeyPress: boolean = false;
  size: string;
  body: string;
  fullPageModal: boolean = false;
  modalFullpage: string = '';
  extraClasses: string = '';

  constructor(private modalService: ModalService, private router: Router) {
    this.modalSubscription = this.modalService.modalType.subscribe((config: ModalConfig) => {
      if (config !== null) {
        this.modalType = config.type;
        this.resolveModalType(this.modalType);
        this.componentInstance.instance.data = config;
        this.modalDisplay = 'modal-container--shown';
        this.size = '__modal';
        this.body = '__body';

        // Adding a class to allow the modal to be scrollable and fix the position of the body content.
        document.querySelector('body').classList.add('modal-container__fullpage');

        if (config.notShowClose === true) {
          this.showClose = false;
        }
        this.disableEscapeKeyPress = config.disableEscapeKeyPress;

        if (this.isFullPageModalType(this.modalType)) {
          this.fullPageModal = true;
          this.componentInstance.instance.dismiss = this.destroyComponent.bind(this);
          this.showClose = false;
          this.size = '__widget';
          this.body = '__bodyWidget';
          this.modalFullpage = 'modal-fullpage';
          //if this is a widget, change the modalDisplay class to remove modal

          if (
            this.modalType !== ModalType.esignModal &&
            this.modalType !== ModalType.clientEsign &&
            this.modalType !== ModalType.paymentIfMissing
          ) {
            this.modalDisplay = this.modalDisplay + '-no-scroll';
          }
        }

        this.extraClasses = config.extraClasses ? config.extraClasses : '';
        this.modalDisplay += config.backgroundClass ? ` ${config.backgroundClass}` : '';
      } else {
        this.destroyComponent();
      }
    });
  }

  isFullPageModalType(modalType: ModalType): boolean {
    switch (modalType) {
      case ModalType.bankInfo:
      case ModalType.paymentIfMissing:
      case ModalType.beneWidget:
      case ModalType.contactWidget:
      case ModalType.contactWidgetMmDirect:
      case ModalType.clientEsign:
      case ModalType.documentUpload:
      case ModalType.esignModal:
      case ModalType.paymentModal:
      case ModalType.clientConversionEsign:
      case ModalType.AuthorizePayment:
        return true;
      default:
        return false;
    }
  }

  initialize() {
    // should be called inside ngOnInit
    /**
     * Reason: If the page/url changes, then modal should close
     * When Angular router cancels navigation, NavigationCancel gets emitted, but the component's lifecycle methods do not
     * get called, this leads to UI inconsistency where modal gets remove even if the component is still active.
     * So we have to make sure navigation has gone through before we can deactivate the modal.
     */
    this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.componentInstance) {
        this.deactivate();
      }
    });
  }

  abstract resolveModalType(modalType: ModalType): void;

  protected destroyComponent() {
    if (
      this.componentInstance &&
      this.componentInstance.instance &&
      typeof this.componentInstance.instance.preDeactivate === 'function'
    ) {
      this.componentInstance.instance.preDeactivate();
    }
    this.modalRef.remove();
    this.componentInstance = null;
    this.extraClasses = '';
    this.modalType = ModalType.null;
    this.modalDisplay = '';
    this.showClose = true;
    document.querySelector('body').classList.remove('modal-container__fullpage');
  }

  deactivate() {
    let postDeactivateFn;

    if (
      this.componentInstance &&
      this.componentInstance.instance &&
      typeof this.componentInstance.instance.postDeactivate === 'function'
    ) {
      postDeactivateFn = this.componentInstance.instance.postDeactivate;
    }

    this.destroyComponent();

    if (postDeactivateFn) {
      postDeactivateFn(this.modalService);
    }
  }

  keyDownPress(event: KeyboardEvent): void | boolean {
    if (event.code === 'Escape' && !this.disableEscapeKeyPress) {
      this.deactivate();
      return true;
    }
  }
}
